import { Injectable, signal } from '@angular/core';
import { isSafeEmbedUrl } from '../utils/exercise-display.utils';

/**
 * How the viewer has to render the media: an `<img>` or a `<video>`.
 *
 * There is no iframe/embed case. Videos used to be Vimeo references and needed
 * a provider player; they are files on our own infrastructure now, served
 * statically with Range support, so a `<video src>` plays and seeks them.
 */
export type MediaViewerKind = 'image' | 'video';

export interface MediaViewerState {
  visible: boolean;
  kind: MediaViewerKind;
  src: string | null;
  title?: string;
}

const CLOSED: MediaViewerState = {
  visible: false,
  kind: 'image',
  src: null,
  title: undefined,
};

/**
 * The app's single media lightbox.
 *
 * Images and videos used to open in two different overlays — PrimeNG's
 * `p-image` preview for one, a bespoke dialog for the other — so the same
 * gesture on two assets of the same exercise behaved differently. Both now go
 * through here, and callers trigger it with `[appOpenMedia]`.
 *
 * It is also the one place that vets a URL before it reaches the DOM, which is
 * why nothing outside builds an `<iframe src>` of its own.
 */
@Injectable({ providedIn: 'root' })
export class MediaViewerService {
  readonly state = signal<MediaViewerState>(CLOSED);

  openImage(url: string, title?: string): void {
    if (!isSafeEmbedUrl(url)) return;
    this.state.set({ visible: true, kind: 'image', src: url, title });
  }

  /**
   * Opens a stored video file.
   *
   * A video will only play if the object it is stored as kept its extension:
   * assets are served statically and the `Content-Type` comes from that
   * extension alone, so an extensionless object arrives as
   * `application/octet-stream` and the browser downloads it instead of playing
   * it. An `<img>` survives that (it sniffs the bytes), which is why the
   * catalog thumbnails render despite having no extension; `<video>` does not.
   */
  openVideo(url: string, title?: string): void {
    // A `javascript:`/`data:` scheme has no business reaching the DOM even in a
    // src Angular would sanitize anyway.
    if (!isSafeEmbedUrl(url)) return;
    this.state.set({ visible: true, kind: 'video', src: url, title });
  }

  close(): void {
    this.state.set(CLOSED);
  }
}
